import { useCallback, useEffect, useRef } from 'react';

/**
 * Hook that returns a function to check whether the component is currently mounted.
 * Useful for avoiding state updates on unmounted components in async operations.
 *
 * @returns A function that returns `true` while mounted, `false` after unmount
 *
 * @example
 * const isMounted = useIsMounted();
 *
 * const fetchData = async () => {
 *   const result = await someAsyncOperation();
 *   if (isMounted()) {
 *     setState(result);
 *   }
 * };
 */
const useIsMounted = (): () => boolean => {
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return useCallback(() => isMountedRef.current, []);
};

export default useIsMounted;
